// Square Web Payments SDK Integration
// Documentation: https://developer.squareup.com/docs/web-payments/overview

export interface SquareConfig {
  applicationId: string
  locationId: string
  environment: 'sandbox' | 'production'
}

export interface PaymentResult {
  token?: string
  verificationToken?: string
  payment?: any
  errors?: Array<{
    category: string
    code: string
    detail: string
    field?: string
  }>
}

export interface PaymentRequest {
  amount: number // In cents
  currency: 'USD'
  requestId: string // Idempotency key
}

export interface SquarePayment {
  id: string
  status: 'APPROVED' | 'PENDING' | 'COMPLETED' | 'CANCELED' | 'FAILED'
  receiptNumber?: string
  receiptUrl?: string
}

// Square configuration from environment variables
export const getSquareConfig = (): SquareConfig => {
  const applicationId = import.meta.env.VITE_SQUARE_APPLICATION_ID
  const locationId = import.meta.env.VITE_SQUARE_LOCATION_ID
  const environment = import.meta.env.VITE_SQUARE_ENVIRONMENT as 'sandbox' | 'production'

  if (!applicationId || !locationId) {
    throw new Error('Square configuration missing. Please set VITE_SQUARE_APPLICATION_ID and VITE_SQUARE_LOCATION_ID')
  }

  return {
    applicationId,
    locationId,
    environment: environment || 'sandbox'
  }
}

// Initialize Square Web Payments SDK
export const initializeSquare = async (): Promise<any> => {
  const config = getSquareConfig()

  // Load Square Web Payments SDK if not already loaded
  if (!window.Square) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      // Task 4: Use environment-based CDN URL (production vs sandbox)
      const cdnUrl = config.environment === 'production'
        ? 'https://web.squarecdn.com/v1/square.js'
        : 'https://sandbox.web.squarecdn.com/v1/square.js'

      script.src = cdnUrl
      script.onload = () => {
        if (window.Square) {
          resolve(window.Square)
        } else {
          reject(new Error('Square SDK failed to load'))
        }
      }
      script.onerror = () => reject(new Error('Failed to load Square SDK'))
      document.head.appendChild(script)
    })
  }

  return window.Square
}

// Create payment form
export const createPaymentForm = async (containerId: string, amount: number, label: string = 'Tailtations SECRET') => {
  const Square = await initializeSquare()
  const config = getSquareConfig()

  const payments = Square.payments(config.applicationId, config.locationId)
  
  // Initialize Card payment method with minimal valid styling
  // Note: Setting postalCode to empty string forces the field to be visible
  // This fixes an issue where Amex cards (15 digits) don't trigger the ZIP field
  const card = await payments.card({
    style: {
      // Only use valid Square SDK selectors and properties
      '.input-container': {
        borderColor: '#d1d5db',
        borderRadius: '8px'
      },
      '.message-text': {
        color: '#1F2937'
      }
    },
    postalCode: ''
  })
  await card.attach(`#${containerId}`)

  // Create PaymentRequest object for digital wallets (Apple Pay, Google Pay)
  // Must use payments.paymentRequest() to create a proper PaymentRequest instance
  const paymentRequestOptions = {
    countryCode: 'US',
    currencyCode: 'USD',
    total: {
      amount: (amount / 100).toFixed(2),
      label
    }
  }
  const paymentRequest = payments.paymentRequest(paymentRequestOptions)

  // Initialize Apple Pay if available
  let applePay = null
  try {
    applePay = await payments.applePay(paymentRequest)
    console.log('Apple Pay initialized successfully')
  } catch (e: any) {
    console.log('Apple Pay not available:', e?.message || e)
  }

  // Initialize Google Pay if available
  let googlePay = null
  try {
    googlePay = await payments.googlePay(paymentRequest)
    console.log('Google Pay initialized successfully')
  } catch (e: any) {
    console.log('Google Pay not available:', e?.message || e)
  }

  return {
    card,
    applePay,
    googlePay,
    payments
  }
}

// Process payment
export const processPayment = async (
  paymentMethod: any,
  amount: number,
  orderId: string
): Promise<PaymentResult> => {
  try {
    const result = await paymentMethod.tokenize()

    if (result.status === 'OK') {
      // Return token and verification token (for 3D Secure)
      return {
        token: result.token,
        verificationToken: result.details?.verificationToken
      }
    } else {
      return {
        errors: result.errors
      }
    }
  } catch (error: any) {
    return {
      errors: [{
        category: 'PAYMENT_METHOD_ERROR',
        code: 'GENERIC_DECLINE',
        detail: error.message || 'Payment failed'
      }]
    }
  }
}

// Format price for display
export const formatPrice = (cents: number): string => {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  })
}

// Generate idempotency key
export const generateIdempotencyKey = (): string => {
  return `secret_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Validate payment amount
export const validatePaymentAmount = (amount: number): boolean => {
  // Square minimum is $1.00 USD
  return amount >= 100 && amount <= 99999999 // Max $999,999.99
}

// Square error handler
export const handleSquareError = (errors: any[]): string => {
  if (!errors || errors.length === 0) {
    return 'Payment failed. Please try again.'
  }

  const error = errors[0]
  
  switch (error.code) {
    case 'CVV_FAILURE':
      return 'Please check your card\'s security code and try again.'
    case 'ADDRESS_VERIFICATION_FAILURE':
      return 'Please verify your billing address.'
    case 'GENERIC_DECLINE':
      return 'Your payment was declined. Please try a different card.'
    case 'INSUFFICIENT_FUNDS':
      return 'Insufficient funds. Please try a different card.'
    case 'INVALID_EXPIRATION':
      return 'Please check your card\'s expiration date.'
    case 'CARD_NOT_SUPPORTED':
      return 'This card type is not supported.'
    default:
      return error.detail || 'Payment failed. Please try again.'
  }
}

// Type declaration for Square SDK
declare global {
  interface Window {
    Square: any
  }
}